// route-url — the minimal URL <-> view mapping that makes every talent view
// deep-linkable WITHOUT a router (doctrine: no router library; mirrors web's
// lib/overlay-url.ts). App.tsx seeds the store from the path at boot, pushes
// the matching path when the store changes, and syncs back on `popstate`.
// Keep pathToRoute/routeToPath total and inverse on the valid set — the
// round-trip test in route-url.test.ts pins that.
//
// Canonical paths:
//   /                          → directory (no identity, the project picker)
//   /s/:sellerId               → inbox (identity set, no thread open)
//   /s/:sellerId/t/:threadId   → open thread
//
// Ids are arbitrary strings (Fiverr usernames), so dynamic segments are
// encodeURIComponent'ed on format and safe-decoded on parse.

/** The three navigable views — exactly the store's (activeSellerId, selectedThreadId) shapes. */
export type SellerRoute =
  | { kind: "directory" }
  | { kind: "inbox"; sellerId: string }
  | { kind: "thread"; sellerId: string; threadId: string };

// decodeURIComponent throws URIError on malformed input ("/s/%zz") — treat as unknown.
function safeDecode(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Map a pathname to a route, or null for anything unrecognized (the caller
 * falls back to the directory). Trailing slash tolerated, never emitted.
 */
export function pathToRoute(pathname: string): SellerRoute | null {
  if (pathname === "/" || pathname === "") return { kind: "directory" };

  const m = /^\/s\/([^/]+)(?:\/t\/([^/]+))?\/?$/.exec(pathname);
  if (!m) return null;
  const sellerId = safeDecode(m[1]);
  if (sellerId === null) return null;
  if (m[2] === undefined) return { kind: "inbox", sellerId };
  const threadId = safeDecode(m[2]);
  if (threadId === null) return null;
  return { kind: "thread", sellerId, threadId };
}

/** Inverse of pathToRoute on every valid route (canonical encoding). */
export function routeToPath(route: SellerRoute): string {
  if (route.kind === "directory") return "/";
  if (route.kind === "inbox") return `/s/${encodeURIComponent(route.sellerId)}`;
  return `/s/${encodeURIComponent(route.sellerId)}/t/${encodeURIComponent(route.threadId)}`;
}

/** The route the store's session state denotes (the two-pane ternary in App.tsx). */
export function routeForState(
  activeSellerId: string,
  selectedThreadId: string | null,
): SellerRoute {
  if (!activeSellerId) return { kind: "directory" };
  if (selectedThreadId) {
    return { kind: "thread", sellerId: activeSellerId, threadId: selectedThreadId };
  }
  return { kind: "inbox", sellerId: activeSellerId };
}
